import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import CertificateLink, { certificateFileName } from '../components/CertificateLink';
import { useAuth } from '../context/AuthContext';
import { overlayDismiss } from '../utils/modalDismiss';
import CertificateTemplateModal from '../components/CertificateTemplateModal';
import { BatchSettingsModal } from './CertificateBatch';
import { THEME_NAMES, LEVEL_NAMES, download, openPdf, formatDate, emailSelected } from '../utils/certificates';

const TABS = [['issued', 'Issued'], ['batches', 'Imported lists'], ['exams', 'Exams'], ['templates', 'Templates']];

// ---------------------------------------------------------------------------
// Actions on one issued certificate, shared by every list that shows them.
// ---------------------------------------------------------------------------
export const useCertificateActions = (reload) => {
  const view = (c) => openPdf((opts) => api.get(`/certificates/${c.id}/pdf`, opts));
  const save = (c) => download(`/certificates/${c.id}/pdf`, certificateFileName(c, c.recipient_name));
  const revoke = async (c) => {
    const reason = window.prompt(`Withdraw certificate ${c.number} for ${c.recipient_name}?\n\nIt will show as withdrawn when anyone verifies it. Reason:`);
    if (!reason) return;
    try {
      await api.post(`/certificates/${c.id}/revoke`, { reason });
      reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to withdraw');
    }
  };
  const reissue = async (c) => {
    const name = window.prompt(
      `Reissue ${c.number}? The old certificate is withdrawn and a new one with a new number is issued.\n\nName as it should be printed:`,
      c.recipient_name
    );
    if (!name) return;
    try {
      const { data } = await api.post(`/certificates/${c.id}/reissue`, { recipient_name: name, reason: 'Reissued' });
      alert(`New certificate ${data.number} issued`);
      reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reissue');
    }
  };
  return { view, save, revoke, reissue };
};

const StatusBadge = ({ c }) => (c.revoked_at
  ? <span className="badge badge-danger" title={c.revoked_reason || ''}>Withdrawn</span>
  : <span className="badge badge-success">Valid</span>);

// ---------------------------------------------------------------------------
// Issued register
// ---------------------------------------------------------------------------
const IssuedTab = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [showRevoked, setShowRevoked] = useState(false);
  const load = useCallback(() => { api.get('/certificates').then(r => setRows(r.data)).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  const act = useCertificateActions(load);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || []).filter(c => (showRevoked || !c.revoked_at) && (!needle ||
      [c.number, c.recipient_name, c.recipient_email, c.course_title, c.employee_id].some(v => (v || '').toLowerCase().includes(needle))));
  }, [rows, q, showRevoked]);

  if (!rows) return <div className="text-center mt-5">Loading...</div>;
  return (
    <div>
      <div className="flex flex-center gap-3 mb-3">
        <input type="search" placeholder="Search name, email, number, course" value={q} onChange={e => setQ(e.target.value)}
          style={{ flex: 1, maxWidth: 420, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }} />
        <label className="flex flex-center gap-2 text-sm">
          <input type="checkbox" checked={showRevoked} onChange={e => setShowRevoked(e.target.checked)} /> Show withdrawn
        </label>
        <span className="text-sm text-muted">{shown.length} certificate{shown.length === 1 ? '' : 's'}</span>
      </div>
      {shown.length === 0 ? (
        <div className="card empty-state">
          <h3>No certificates yet</h3>
          <p>Certificates appear here once issued from an exam or a training.</p>
        </div>
      ) : (
        <div className="card table-scroll" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>Number</th><th>Name</th><th>Type</th><th>Course</th><th>Issued</th><th>Emailed</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {shown.map(c => (
                <tr key={c.id}>
                  <td className="text-sm"><code>{c.number}</code></td>
                  <td><strong>{c.recipient_name}</strong><div className="text-sm text-muted">{c.recipient_email}</div></td>
                  <td className="text-sm">{LEVEL_NAMES[c.level]}</td>
                  <td className="text-sm">{c.course_title}{c.company_name && <div className="text-muted">{c.company_name}</div>}</td>
                  <td className="text-sm">{formatDate(c.issued_at)}</td>
                  <td className="text-sm">{c.emailed_at ? formatDate(c.emailed_at) : '—'}</td>
                  <td><StatusBadge c={c} /></td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => act.view(c)}>View</button>{' '}
                    <button className="btn btn-secondary btn-sm" onClick={() => act.save(c)}>Download</button>{' '}
                    <a className="btn btn-secondary btn-sm" href={`/verify/${encodeURIComponent(c.number)}`} target="_blank" rel="noopener noreferrer"
                      title="Open the public page an employer or auditor sees when they scan the QR code">Verify ↗</a>{' '}
                    {!c.revoked_at && <>
                      <button className="btn btn-secondary btn-sm" onClick={() => act.reissue(c)}>Reissue</button>{' '}
                      <button className="btn btn-danger btn-sm" onClick={() => act.revoke(c)}>Withdraw</button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-exam settings and recipients
// ---------------------------------------------------------------------------
const QuizCertificatesModal = ({ quiz, templates, onClose, onChanged }) => {
  const [settings, setSettings] = useState({
    template_id: quiz.certificate_template_id || '',
    attendance_template_id: quiz.certificate_attendance_template_id || '',
    company: quiz.certificate_company || ''
  });
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const loadRows = useCallback(() => {
    api.get(`/certificates/quizzes/${quiz.id}/recipients`).then(r => setRows(r.data)).catch(() => setRows([]));
  }, [quiz.id]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put(`/certificates/quizzes/${quiz.id}`, settings);
      onChanged();
      loadRows();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const issueAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/certificates/quizzes/${quiz.id}/issue`);
      alert(`${data.achievement} completion and ${data.attendance} attendance certificate(s) issued. Nothing has been emailed.`);
      loadRows();
      onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to issue');
    } finally {
      setBusy(false);
    }
  };

  // Each issued certificate as a flat list, for selection and email.
  const certs = (rows || []).flatMap(r => ['achievement', 'attendance']
    .filter(l => r[l]?.state === 'issued')
    .map(l => ({ id: r[l].id, number: r[l].number, level: l, emailed_at: r[l].emailed_at, recipient_name: r.candidate_name, recipient_email: r.candidate_email })));
  const toggle = (id) => setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const chosen = certs.filter(c => selected.has(c.id));
  const saved = String(settings.template_id || '') === String(quiz.certificate_template_id || '') &&
    String(settings.attendance_template_id || '') === String(quiz.certificate_attendance_template_id || '') &&
    (settings.company || '') === (quiz.certificate_company || '');

  const cell = (r, level) => {
    const c = r[level];
    if (!c) return <span className="text-muted">—</span>;
    if (c.state === 'available') return <span className="text-sm text-muted">Not issued yet</span>;
    if (c.state === 'revoked') return <span className="badge badge-danger">Withdrawn</span>;
    return (
      <label className="flex flex-center gap-2 text-sm">
        <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
        <CertificateLink cert={c} name={r.candidate_name} />
        {c.emailed_at && <span className="text-muted" title={`Emailed ${formatDate(c.emailed_at)}`}>✉</span>}
      </label>
    );
  };

  return (
    <div className="modal-overlay" {...overlayDismiss(onClose)}>
      <div className="modal" style={{ maxWidth: 900, maxHeight: '92vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h3>Certificates: {quiz.title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="grid grid-3">
            <div className="form-group">
              <label>Completion (passed)</label>
              <select value={settings.template_id} onChange={e => setSettings({ ...settings, template_id: e.target.value })}>
                <option value="">None</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Attendance (everyone who completed)</label>
              <select value={settings.attendance_template_id} onChange={e => setSettings({ ...settings, attendance_template_id: e.target.value })}>
                <option value="">None</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Company (customer)</label>
              <input type="text" maxLength={255} placeholder="e.g. Dr. Reddy's Laboratories Ltd" value={settings.company}
                onChange={e => setSettings({ ...settings, company: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-2 mb-4">
            <button className="btn btn-primary btn-sm" onClick={saveSettings} disabled={busy || saved}>Save settings</button>
            <span className="text-sm text-muted" style={{ alignSelf: 'center' }}>
              Candidates download their certificates from their result page.
            </span>
          </div>

          <div className="flex flex-between flex-center mb-2">
            <h4 style={{ margin: 0 }}>Candidates ({rows ? rows.length : '…'})</h4>
            <div className="flex gap-2">
              <button className="btn btn-secondary btn-sm" onClick={issueAll} disabled={busy || !saved || !rows?.some(r => r.achievement?.state === 'available' || r.attendance?.state === 'available')}>
                Issue all not yet issued
              </button>
              <button className="btn btn-secondary btn-sm" disabled={!certs.length}
                onClick={() => download(`/certificates/quizzes/${quiz.id}/zip`, `Certificates_${quiz.title.replace(/[^a-zA-Z0-9]+/g, '_')}.zip`)}>
                Download all (ZIP)
              </button>
              <button className="btn btn-primary btn-sm" disabled={!chosen.length}
                onClick={() => emailSelected(`/certificates/quizzes/${quiz.id}/email`, chosen, () => { setSelected(new Set()); loadRows(); })}>
                Email selected ({chosen.length})
              </button>
            </div>
          </div>
          {!rows ? <p className="text-muted">Loading...</p> : rows.length === 0 ? (
            <p className="text-muted">No candidates qualify yet{saved && !quiz.certificate_template_id && !quiz.certificate_attendance_template_id ? ' — choose a template above' : ''}.</p>
          ) : (
            <table className="table">
              <thead><tr>
                <th>
                  <label className="flex flex-center gap-2" title="Select every issued certificate">
                    <input type="checkbox" checked={certs.length > 0 && chosen.length === certs.length}
                      onChange={e => setSelected(e.target.checked ? new Set(certs.map(c => c.id)) : new Set())} />
                    Candidate
                  </label>
                </th><th>Score</th>
                <th>Completion</th>
                <th>Attendance</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.attempt_id}>
                    <td><strong>{r.candidate_name}</strong><div className="text-sm text-muted">{r.candidate_email}</div></td>
                    <td className="text-sm">
                      {r.status === 'submitted' ? 'Awaiting grading' : `${Number(r.percentage).toFixed(0)}%`}
                      {r.passed && <span className="badge badge-success" style={{ marginLeft: 6 }}>Passed</span>}
                    </td>
                    <td>{cell(r, 'achievement')}</td>
                    <td>{cell(r, 'attendance')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-sm text-muted mt-2">Tick certificates, then "Email selected". Nothing is emailed automatically.</p>
        </div>
      </div>
    </div>
  );
};

const ExamsTab = ({ templates }) => {
  const [quizzes, setQuizzes] = useState(null);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState('');
  const load = useCallback(() => { api.get('/certificates/quizzes').then(r => setQuizzes(r.data)).catch(() => setQuizzes([])); }, []);
  useEffect(() => { load(); }, [load]);
  const name = (id) => templates.find(t => t.id === id)?.name;

  if (!quizzes) return <div className="text-center mt-5">Loading...</div>;
  const needle = q.trim().toLowerCase();
  const shown = quizzes.filter(z => !needle || z.title.toLowerCase().includes(needle) || (z.category_name || '').toLowerCase().includes(needle));
  return (
    <div>
      <p className="text-muted mb-3">
        Choose which exams give certificates. Candidates who pass get the completion certificate; with an attendance
        template, everyone who completes the exam also gets a certificate of attendance.
      </p>
      <input type="search" placeholder="Search exams" value={q} onChange={e => setQ(e.target.value)} className="mb-3"
        style={{ width: '100%', maxWidth: 420, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }} />
      <div className="card table-scroll" style={{ padding: 0 }}>
        <table className="table">
          <thead><tr><th>Exam</th><th>Completion</th><th>Attendance</th><th>Company</th><th>Passed</th><th>Issued</th><th></th></tr></thead>
          <tbody>
            {shown.map(z => (
              <tr key={z.id} style={{ cursor: 'pointer' }} onClick={() => setOpen(z)}>
                <td><strong>{z.title}</strong>{z.category_name && <div className="text-sm text-muted">{z.category_name}</div>}</td>
                <td className="text-sm">{name(z.certificate_template_id) || <span className="text-muted">—</span>}</td>
                <td className="text-sm">{name(z.certificate_attendance_template_id) || <span className="text-muted">—</span>}</td>
                <td className="text-sm">{z.certificate_company || <span className="text-muted">—</span>}</td>
                <td>{z.passed_count}</td>
                <td>{z.issued_count}</td>
                <td className="text-muted" style={{ textAlign: 'right' }}>Settings ›</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <QuizCertificatesModal quiz={quizzes.find(z => z.id === open.id) || open} templates={templates}
        onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Batches: certificates for an imported list of people
// ---------------------------------------------------------------------------
const BatchesTab = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState(null);
  const [creating, setCreating] = useState(false);
  useEffect(() => { api.get('/certificate-batches').then(r => setBatches(r.data)).catch(() => setBatches([])); }, []);

  if (!batches) return <div className="text-center mt-5">Loading...</div>;
  return (
    <div>
      <div className="flex flex-between flex-center mb-3" style={{ gap: 12 }}>
        <p className="text-muted" style={{ margin: 0 }}>
          For trainings without QR attendance: import a list of trainees, choose who gets completion + attendance
          and who gets attendance only, then issue.
        </p>
        <button className="btn btn-primary" onClick={() => setCreating(true)} style={{ whiteSpace: 'nowrap' }}>+ New list</button>
      </div>
      {batches.length === 0 ? (
        <div className="card empty-state">
          <h3>No imported lists yet</h3>
          <p>Create one, then upload an Excel or CSV file of trainees.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>Course</th><th>Dates</th><th>Company</th><th>People</th><th>Issued</th><th>Created</th></tr></thead>
            <tbody>
              {batches.map(b => (
                <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/certificates/batches/${b.id}`)}>
                  <td><strong>{b.title}</strong></td>
                  <td className="text-sm">{b.dates_text || '—'}</td>
                  <td className="text-sm">{b.company || '—'}</td>
                  <td>{b.people_count}</td>
                  <td>{b.issued_count}</td>
                  <td className="text-sm text-muted">{formatDate(b.created_at)}{b.created_by_name && ` · ${b.created_by_name}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && <BatchSettingsModal onClose={() => setCreating(false)} onSaved={(b) => navigate(`/certificates/batches/${b.id}`)} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------
const TemplatesTab = ({ templates, reload, isAdmin }) => {
  const [editing, setEditing] = useState(null); // template, or {} for new

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/certificates/templates/${t.id}`);
      reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex flex-between flex-center mb-3">
        <p className="text-muted" style={{ margin: 0 }}>A template holds the design, wording and signatories.</p>
        {isAdmin && <button className="btn btn-primary" onClick={() => setEditing({})}>+ New template</button>}
      </div>
      {templates.length === 0 ? (
        <div className="card empty-state">
          <h3>No templates yet</h3>
          <p>{isAdmin ? 'Create one for completion and one for attendance.' : 'Ask an admin to create a certificate template.'}</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>Name</th><th>Design</th><th>Small heading</th><th>Used by</th><th>Issued</th><th></th></tr></thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.id}>
                  <td>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: t.accent_color, marginRight: 8 }} />
                    <strong>{t.name}</strong>
                  </td>
                  <td className="text-sm">{THEME_NAMES[t.theme]}</td>
                  <td className="text-sm">{t.kicker}</td>
                  <td className="text-sm">{t.quiz_count} exam{t.quiz_count === 1 ? '' : 's'}, {t.training_count} training{t.training_count === 1 ? '' : 's'}, {t.batch_count} list{t.batch_count === 1 ? '' : 's'}</td>
                  <td>{t.issued_count}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(t)}>{isAdmin ? 'Edit' : 'View'}</button>{' '}
                    {isAdmin && !t.issued_count && <button className="btn btn-danger btn-sm" onClick={() => remove(t)}>Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <CertificateTemplateModal
          template={editing.id ? editing : null}
          readOnly={!isAdmin}
          onClose={() => { setEditing(null); reload(); }}
          onSaved={reload}
        />
      )}
    </div>
  );
};

const Certificates = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'issued';
  const setTab = (k) => setParams({ tab: k }, { replace: true });
  const [templates, setTemplates] = useState([]);
  const loadTemplates = useCallback(() => { api.get('/certificates/templates').then(r => setTemplates(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  return (
    <div>
      <div className="mb-4">
        <div className="flex flex-between flex-center" style={{ gap: 12 }}>
          <h1 style={{ margin: 0 }}>Certificates</h1>
          <a className="btn btn-secondary btn-sm" href="/verify" target="_blank" rel="noopener noreferrer">Verify a certificate ↗</a>
        </div>
        <p className="text-muted">
          Exam certificates are set up here; training certificates (e.g. CPI) on each training's page under
          {' '}<Link to="/attendance">Attendance</Link>.
        </p>
      </div>
      <div className="flex gap-2 mb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className="btn btn-sm"
            style={{
              background: 'none', borderRadius: 0, padding: '8px 14px', color: tab === k ? '#ea580c' : '#4b5563',
              borderBottom: tab === k ? '2px solid #ea580c' : '2px solid transparent', fontWeight: tab === k ? 600 : 400
            }}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'issued' && <IssuedTab />}
      {tab === 'batches' && <BatchesTab />}
      {tab === 'exams' && <ExamsTab templates={templates} />}
      {tab === 'templates' && <TemplatesTab templates={templates} reload={loadTemplates} isAdmin={user?.role === 'admin'} />}
    </div>
  );
};

export default Certificates;

